import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Download } from "lucide-react";
import { IMAGES, WA, PRICES, DATASHEET } from "@/content";
import { scrollToId } from "@/lib/scroll";
import { Eyebrow } from "@/components/Reveal";
import { CallButton } from "@/components/CallButton";

const ease = [0.22, 1, 0.36, 1];

function MaskLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const CHIPS = [
  { label: "60 muestras / hora", cls: "top-[12%] -left-[4%]", d: 0 },
  { label: "Micromuestra 9,6 µl", cls: "top-[38%] -right-[6%]", d: 0.9 },
  { label: "Solo 2 reactivos", cls: "bottom-[16%] -left-[8%]", d: 1.8 },
];

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rxF = useTransform(my, [-0.5, 0.5], [8, -8]);
  const ryF = useTransform(mx, [-0.5, 0.5], [-10, 10]);
  const rx = useSpring(rxF, { stiffness: 55, damping: 14 });
  const ry = useSpring(ryF, { stiffness: 55, damping: 14 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yDev = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative overflow-hidden pt-[120px] lg:pt-[136px] pb-16 lg:pb-24 bg-brand-cloud"
    >
      <div className="absolute inset-0 dotgrid opacity-40" aria-hidden="true" />
      <motion.div
        aria-hidden="true"
        style={{ y: yGlow }}
        className="absolute -top-32 right-[-10%] w-[720px] h-[720px] rounded-full bg-[radial-gradient(circle,rgba(255,87,34,0.22),transparent_62%)] blur-2xl"
      />
      <div
        aria-hidden="true"
        className="absolute top-24 left-[-12%] w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle,rgba(2,132,199,0.14),transparent_65%)] blur-2xl"
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-6 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
            <Eyebrow>Lanzamiento en Colombia · Unidades limitadas</Eyebrow>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold tracking-[-0.02em] leading-[1.04] text-[2.6rem] sm:text-6xl xl:text-[4.6rem] text-brand-ink mt-6"
          >
            <MaskLine delay={0.15}>Hematología</MaskLine>
            <MaskLine delay={0.27}>
              <span className="text-brand-orange">veterinaria</span> completa,
            </MaskLine>
            <MaskLine delay={0.39}>en un solo equipo.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
          >
            <strong className="text-brand-ink font-bold">Excbio EH8300 VET</strong>: analizador automático de
            hematología con 24 parámetros + 3 histogramas, sistema abierto y el respaldo técnico de Hamburger Invitro.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.72, ease }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href={WA.quote}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta-button"
              className="btn-shine group inline-flex items-center gap-2.5 rounded-full bg-brand-orange text-white font-bold text-[15px] px-7 py-4 shadow-[0_18px_40px_-14px_rgba(255,87,34,0.6)] hover:bg-brand-deep transition-colors duration-300"
            >
              <MessageCircle className="h-5 w-5" />
              Cotizar por WhatsApp
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <button
              data-testid="hero-specs-scroll-button"
              onClick={() => scrollToId("#specs")}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/70 px-7 py-4 text-[15px] font-bold text-brand-ink hover:border-brand-orange hover:text-brand-orange transition-colors duration-300"
            >
              Ver ficha técnica
            </button>
            <CallButton variant="light" iconOnly testid="hero-call-button" />
          </motion.div>

          <motion.a
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            href={DATASHEET.url}
            download={DATASHEET.filename}
            data-testid="hero-download-datasheet-button"
            className="group mt-5 inline-flex items-center gap-2 text-sm font-bold text-brand-ink hover:text-brand-orange transition-colors duration-300"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full border border-slate-300 bg-white/70 group-hover:border-brand-orange transition-colors duration-300">
              <Download className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5" />
            </span>
            <span className="underline decoration-slate-300 underline-offset-4 group-hover:decoration-brand-orange transition-colors duration-300">
              Descargar ficha técnica en PDF
            </span>
            <span className="font-mono2 text-[11px] text-slate-400">· 220 KB</span>
          </motion.a>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] font-semibold text-slate-500"
          >
            <span data-testid="hero-trust-installation">✓ Instalación y capacitación incluidas</span>
            <span data-testid="hero-trust-warranty">✓ Garantía de 1 año</span>
            <span data-testid="hero-trust-support">✓ Soporte de ingeniero biomédico</span>
          </motion.div>
        </div>

        <div className="relative">
          <motion.div
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[82%] w-[82%] rounded-full border border-dashed border-brand-orange/30 animate-spin-slow"
          />
          <motion.div style={{ y: yDev }} className="relative z-10 [perspective:1000px]">
            <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
              <motion.img
                src={IMAGES.hero}
                alt="Analizador automático de hematología veterinaria Excbio EH8300 VET"
                data-testid="hero-device-image"
                initial={{ opacity: 0, scale: 0.94, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 1.1, delay: 0.35, ease }}
                className="relative z-10 w-full max-w-[520px] mx-auto mix-blend-multiply select-none"
                draggable={false}
              />
            </motion.div>
          </motion.div>

          <div
            aria-hidden="true"
            className="absolute bottom-[6%] left-1/2 -translate-x-1/2 w-[60%] h-10 rounded-[100%] bg-slate-900/15 blur-xl"
          />

          {CHIPS.map((c) => (
            <motion.span
              key={c.label}
              data-testid={`hero-chip-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1 + c.d * 0.25, ease }}
              className={`absolute z-20 ${c.cls} animate-floaty glass border border-white/70 shadow-lg rounded-2xl px-4 py-2.5 font-mono2 text-[11px] sm:text-xs font-medium tracking-wide text-brand-ink whitespace-nowrap`}
            >
              <span className="text-brand-orange mr-1.5">●</span>
              {c.label}
            </motion.span>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.35, ease }}
            data-testid="hero-price-badge"
            className="relative z-20 mt-2 lg:mt-6 mx-auto w-fit flex items-center gap-4 rounded-full bg-brand-ink text-white pl-6 pr-5 py-3.5 shadow-2xl"
          >
            <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-brand-orange">Lanzamiento</span>
            <span className="font-display text-xl sm:text-2xl font-extrabold">
              {PRICES.now} <span className="text-sm font-bold text-slate-300">{PRICES.iva}</span>
            </span>
            <span className="text-sm text-slate-400 line-through">{PRICES.before}</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
